import {
  GoogleMap,
  Marker,
  MarkerClusterer,
  OverlayView,
  useJsApiLoader,
} from '@react-google-maps/api';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { getEnvironment } from 'toolkit/environment/environment/environment';

import { UserForGeoMapDto } from '@act/shared/data-transfer-objects';
import LoaderComponent from 'react-components/workflows/loader';
import { membersAppSelectors } from 'redux/slices/memberSlice';

const MemberMap = () => {
  const [center, setCenter] = useState({ lat: 39.850033, lng: -98.6500523 });
  const [zoom, setZoom] = useState(4);
  const [map, setMap] = useState(null);
  const [hoveredCluster, setHoveredCluster] = useState(null);
  const [hoveredMarker, setHoveredMarker] = useState(null);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-loader-script',
    googleMapsApiKey: getEnvironment().googleMapsApiKey,
  });

  const membersForMap = membersAppSelectors.useSelectAllMembersForMap();

  const onLoad = useCallback(map => setMap(map), []);

  function filterValidMembersWithCoords() {
    if (membersForMap.length === 0) return [];
    return membersForMap.filter(m => {
      return m.coords?.lat && m.coords?.lng;
    });
  }

  useEffect(() => {
    function updateMapOptions() {
      const validMembersWithCoords: UserForGeoMapDto[] =
        filterValidMembersWithCoords();
      const averageLat =
        validMembersWithCoords.reduce((acc: number, member) => {
          return acc + member.coords.lat;
        }, 0) / validMembersWithCoords.length;
      const averageLng =
        validMembersWithCoords.reduce((acc: number, member) => {
          return acc + member.coords.lng;
        }, 0) / validMembersWithCoords.length;

      setCenter({ lat: averageLat, lng: averageLng });
      setZoom(10);

      if (map) {
        const bounds = new window.google.maps.LatLngBounds();
        validMembersWithCoords.forEach(m =>
          bounds.extend({ lat: m.coords.lat, lng: m.coords.lng })
        );
        map.fitBounds(bounds, { top: 50, bottom: 50 });
      }
    }

    if (map && membersForMap.length) {
      updateMapOptions();
    }
  }, [map, membersForMap]);

  return isLoaded && membersForMap.length ? (
    <GoogleMap
      mapContainerStyle={{ width: '100%', height: '60vh' }}
      center={center}
      zoom={zoom}
      options={{
        mapTypeControl: false,
        zoomControlOptions: {
          position: google.maps.ControlPosition.RIGHT_CENTER,
        },
      }}
      onLoad={onLoad}
    >
      <MarkerClusterer
        averageCenter
        gridSize={65}
        maxZoom={14}
        zoomOnClick={false}
        onClick={cluster => {
          setHoveredMarker(null);
          if (hoveredCluster?.getMarkers() === cluster.getMarkers()) {
            setHoveredCluster(null);
          } else {
            setHoveredCluster(cluster);
          }
        }}
        styles={[
          {
            url: `data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciI
            HhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiB2aWV3Qm94PSItMTAwIC0xMDAgMj
            AwIDIwMCI+PGcgZmlsbD0id2hpdGUiPgogICAgICA8Y2lyY2xlIHI9IjUwIi8+PHVzZSB4bGluazpocmVmPSI
            jYSIvPjxnIHRyYW5zZm9ybT0icm90YXRlKDEyMCkiPjx1c2UgeGxpbms6aHJlZj0iI2EiLz48L2c+PGcgdHJh
            bnNmb3JtPSJyb3RhdGUoMjQwKSI+CiAgICAgIDx1c2UgeGxpbms6aHJlZj0iI2EiLz48L2c+PC9nPjwvc3ZnP
            g==`,
            height: 100,
            width: 100,
          },
        ]}
      >
        {clusterer => (
          <Fragment>
            {filterValidMembersWithCoords().map(m => {
              const label = `${m.firstName} ${m.lastName} (${m.memberStatus.name})`;
              const marker = (
                <Marker
                  key={m.id}
                  icon={{
                    path: google.maps.SymbolPath.CIRCLE,
                    scale: 6,
                    strokeWeight: 1.5,
                    fillColor: 'white',
                    fillOpacity: 0.8,
                    strokeColor: 'black',
                  }}
                  onClick={_$e => {
                    setHoveredCluster(null);
                    const hm = hoveredMarker;
                    if (
                      `${hm?.firstName} ${hm?.lastName} (${hm?.memberStatus.name})` ===
                      label
                    ) {
                      setHoveredMarker(null);
                    } else {
                      setHoveredMarker(m);
                    }
                  }}
                  position={{ lat: m.coords.lat, lng: m.coords.lng }}
                  clusterer={clusterer}
                  title={label}
                />
              );
              return marker;
            })}
          </Fragment>
        )}
      </MarkerClusterer>
      {hoveredCluster ? (
        <OverlayView
          mapPaneName="floatPane"
          position={{
            lat: hoveredCluster.getCenter().lat(),
            lng: hoveredCluster.getCenter().lng(),
          }}
        >
          <div
            style={{
              width: 'max-content',
              position: 'absolute',
              right: '50%',
              bottom: '42px',
              padding: '15px 25px',
              boxSizing: 'border-box',
              backgroundColor: '#ffffff',
              borderRadius: '5px',
              transform: 'translateX(50%)',
              pointerEvents: 'none',
            }}
          >
            <div
              style={{
                lineHeight: '15px',
              }}
            >
              {hoveredCluster.getMarkers()?.map(m => (
                <Fragment key={m.title}>
                  <span>{m.title}</span>
                  <br />
                </Fragment>
              ))}
            </div>
          </div>
        </OverlayView>
      ) : null}
      {hoveredMarker ? (
        <OverlayView
          mapPaneName="floatPane"
          position={{
            lat: hoveredMarker.coords.lat,
            lng: hoveredMarker.coords.lng,
          }}
        >
          <div
            style={{
              position: 'relative',
              left: '-50%',
              bottom: '60px',
              padding: '15px 25px',
              backgroundColor: '#ffffff',
              borderRadius: '5px',
              pointerEvents: 'none',
            }}
          >
            {hoveredMarker.firstName} {hoveredMarker.lastName} (
            {hoveredMarker.memberStatus.name})
          </div>
        </OverlayView>
      ) : null}
    </GoogleMap>
  ) : (
    <LoaderComponent />
  );
};

export default MemberMap;
